/**
 * Авторские индикаторы этапа 3: стакан, ликвидации, дельта по сделкам.
 *
 * Видны только когда сервер собирает для них данные: этап 3 включается
 * на сервере отдельным решением — стакан стоит гигабайтов трафика.
 */

import type { Candle, OrderFlowVenue } from '@specter/types';
import { barsSettings } from '../orderflow/bars-settings.js';
import { bookSettings } from '../orderflow/book-settings.js';
import type { DeltaAnchor } from '../orderflow/delta-analysis.js';
import { TABLE_PARAM } from './explain.js';
import { explainBook, explainFlowLiquidations } from './explain-orderflow.js';
import { explainFlow, type FlowExplained } from './explain-flow.js';
import type {
  IndicatorContext,
  IndicatorDefinition,
  IndicatorParams,
  IndicatorParamSpec,
  IndicatorPoint,
} from './types.js';

const COLORS = { buy: '#16C784', sell: '#EA3943' };

function venuesOf(source: unknown): ReadonlySet<OrderFlowVenue> {
  return new Set<OrderFlowVenue>(
    source === 'binance' ? ['binance'] : source === 'bybit' ? ['bybit'] : ['binance', 'bybit'],
  );
}

const SOURCE: IndicatorParamSpec = {
  key: 'source',
  label: 'Биржи',
  hint: 'Обе — весь рынок. Одна — если хочется понять, откуда что пришло.',
  type: 'select',
  default: 'all',
  options: [
    { value: 'all', label: 'Binance и Bybit' },
    { value: 'binance', label: 'Только Binance' },
    { value: 'bybit', label: 'Только Bybit' },
  ],
};

export const ORDER_BOOK_HEATMAP: IndicatorDefinition = {
  id: 'order-book-heatmap',
  name: 'Тепловая карта стакана',
  description:
    'Где в стакане стояли крупные заявки и как долго. Зелёные клетки — ждали покупки, красные — продажи; ' +
    'чем ярче, тем плотнее. Это не сделки: заявки могут снять, так и не исполнив.',
  author: 'specter',
  placement: 'overlay',
  orderBook: true,
  requires: 'book',
  params: [
    {
      ...SOURCE,
      hint:
        'Обе — весь рынок. Bybit отдаёт только ближние к цене заявки, поэтому дальние «стены» видны в основном по Binance.',
    },
    {
      key: 'intensity',
      label: 'Яркость, %',
      hint: 'Насколько сильно горят клетки. Меньше — свечи видно лучше, больше — заметнее слабые уровни.',
      type: 'number',
      default: 70,
      min: 10,
      max: 100,
      step: 5,
    },
    {
      key: 'floor',
      label: 'Не показывать слабее, %',
      hint: 'Клетки бледнее этой доли от самых плотных уровней на экране не рисуются: так остаются только заметные заявки.',
      type: 'number',
      default: 10,
      min: 0,
      max: 90,
      step: 5,
    },
    {
      key: 'walls',
      label: 'Крупные заявки линией',
      hint: 'Заявку, которая стоит подолгу, отметить линией от её появления до исчезновения — с объёмом и тем, сколько она держится.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'wallFactor',
      label: 'Стена — плотнее обычного в',
      hint: 'Во сколько раз заявка плотнее обычного уровня. В стакане разброс невелик: обычный уровень BTC около 12 млн $, самый плотный — 25 млн, поэтому 1,8 это уже стена.',
      type: 'number',
      default: 1.8,
      min: 1.1,
      max: 6,
      step: 0.1,
    },
    {
      key: 'wallMinutes',
      label: 'Стоит не меньше, мин',
      hint: 'Сколько минут заявка должна продержаться. Короткие всплески — это чаще всего робот, который тут же снимает заявку.',
      type: 'number',
      default: 10,
      min: 1,
      max: 720,
      step: 1,
    },
    {
      key: 'wallMinUsd',
      label: 'И не меньше, $',
      hint: 'Дополнительный порог в долларах: 0 — не ограничивать. Удобно, когда нужны только заявки от миллиона.',
      type: 'number',
      default: 0,
      min: 0,
      max: 1_000_000_000,
      step: 100_000,
    },
    {
      key: 'wallLimit',
      label: 'Сколько линий показывать',
      hint: 'На экране остаются только самые крупные стены: десяток линий читается, полсотни — уже лес.',
      type: 'number',
      default: 10,
      min: 1,
      max: 40,
      step: 1,
    },
    {
      key: 'ladder',
      label: 'Стакан сейчас',
      hint: 'Лесенка у правого края: сколько заявок стоит на каждой цене в эту секунду.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'ladderWidth',
      label: 'Ширина лесенки, % графика',
      hint: 'Насколько далеко влево тянутся полосы стакана «сейчас». На расчёт не влияет.',
      type: 'number',
      default: 12,
      min: 3,
      max: 40,
      step: 1,
    },
    {
      key: 'bidColor',
      label: 'Цвет заявок на покупку',
      hint: 'Каким цветом рисовать заявки, которые ждут покупки ниже цены.',
      type: 'color',
      default: '#1FBF8F',
    },
    {
      key: 'askColor',
      label: 'Цвет заявок на продажу',
      hint: 'Каким цветом рисовать заявки, которые ждут продажи выше цены.',
      type: 'color',
      default: '#F0506E',
    },
    TABLE_PARAM,
  ],
  outputs: [],
  warmup: () => 0,
  calc: () => ({}),
  shapes: (_candles, p, context) => {
    const data = context.orderBook;
    if (!data) return [];
    return [{ kind: 'book-heatmap', data, settings: bookSettings(p), key: JSON.stringify(p) }];
  },
  readout: (candles, p, context) => {
    const settings = bookSettings(p);
    return explainBook(candles.at(-1)?.close, context.orderBook?.live, settings.venues, settings.wallFactor);
  },
  label: () => 'Тепловая карта стакана',
};

export const ORDER_FLOW_LIQUIDATIONS: IndicatorDefinition = {
  id: 'order-flow-liquidations',
  name: 'Ликвидации',
  description:
    'Где биржи принудительно закрывали позиции. Красный ромб — вынесло ставки на рост, зелёный — на падение. ' +
    'Скопления ликвидаций часто отмечают разворот или ускорение.',
  author: 'specter',
  placement: 'overlay',
  orderFlow: true,
  requires: 'liquidations',
  params: [
    SOURCE,
    {
      key: 'unit',
      label: 'Считать в',
      hint: 'В чём показывать сумму ликвидаций в карточке: в долларах или в монетах.',
      type: 'select',
      default: 'usd',
      options: [
        { value: 'usd', label: 'Долларах' },
        { value: 'coin', label: 'Монетах' },
      ],
    },
    {
      key: 'minLiquidation',
      label: 'Не меньше, $',
      hint: 'Ликвидации меньше этой суммы не рисуются: мелкие выносы идут постоянно и только засоряют график.',
      type: 'number',
      default: 10_000,
      min: 0,
      max: 10_000_000,
      step: 5_000,
    },
    {
      key: 'buyColor',
      label: 'Цвет выноса коротких',
      hint: 'Вынос ставок на падение — это принудительные покупки, поэтому по умолчанию цвет покупок.',
      type: 'color',
      default: COLORS.buy,
    },
    {
      key: 'sellColor',
      label: 'Цвет выноса длинных',
      hint: 'Вынос ставок на рост — это принудительные продажи, поэтому по умолчанию цвет продаж.',
      type: 'color',
      default: COLORS.sell,
    },
    TABLE_PARAM,
  ],
  outputs: [],
  warmup: () => 0,
  calc: () => ({}),
  shapes: (_candles, p, context) => {
    const data = context.orderFlow;
    if (!data) return [];
    return [
      { kind: 'flow-bars', data, settings: barsSettings(p, 'liquidations'), key: `liquidations|${JSON.stringify(p)}` },
    ];
  },
  readout: (candles, p, context) =>
    explainFlowLiquidations(candles, context.orderFlow, venuesOf(p.source), context.livePartial),
  label: () => 'Ликвидации',
};

/**
 * Дельта по сделкам: перевес покупок над продажами в каждой свече.
 *
 * Отличие от Spot CVD и Futures CVD — источник: здесь это настоящие
 * сделки Binance и Bybit, собранные сервером, а не оценка по свечам.
 * История поэтому начинается с того дня, когда сервер начал слушать, —
 * до этого значения честно пустые.
 *
 * Индикатор обязан отвечать словами, а не только рисовать: столбики
 * читаются сами, а вывод «цена стоит, а продажи идут — их кто-то
 * забирает» человек без опыта не сделает. Поэтому у него табло с
 * описанием ситуации и отметки расхождений прямо на цене. Разбор — в
 * `orderflow/delta-analysis.ts`.
 */
function anchorOf(value: unknown): DeltaAnchor {
  return value === 'week' || value === 'all' ? value : 'day';
}

/** Перевес каждой свечи по выбранным биржам; `null` — сделок нет. */
function barDeltas(
  candles: readonly Candle[],
  p: IndicatorParams,
  context: IndicatorContext,
): (number | null)[] {
  const data = context.orderFlow;
  const venues = venuesOf(p.source);
  return candles.map((candle) => {
    const bar = data?.bar(candle.time);
    // Нет данных — пусто, а не ноль: ноль значил бы «торговали поровну».
    if (!bar) return null;
    let value = 0;
    for (const [venue, levels] of bar.venues) {
      if (!venues.has(venue)) continue;
      for (const cell of levels.values()) value += cell[0]! - cell[1]!;
    }
    return value;
  });
}

/*
 * Разбор считается один раз на отрисовку: `calc`, `shapes` и `readout`
 * зовутся подряд с тем же рядом, и считать трижды незачем.
 */
let memo: { candles: readonly Candle[]; flow: unknown; key: string; result: FlowExplained } | null = null;

function explainedOf(candles: readonly Candle[], p: IndicatorParams, context: IndicatorContext): FlowExplained {
  const key = `${String(p.source)}|${String(p.anchor)}`;
  if (memo && memo.candles === candles && memo.flow === context.orderFlow && memo.key === key) {
    return memo.result;
  }
  const result = explainFlow(candles, barDeltas(candles, p, context), anchorOf(p.anchor));
  memo = { candles, flow: context.orderFlow, key, result };
  return result;
}

export const ORDER_FLOW_DELTA: IndicatorDefinition = {
  id: 'order-flow-delta',
  name: 'Дельта по сделкам',
  description:
    'Кто сильнее прямо сейчас — покупатели или продавцы — по настоящим сделкам Binance и Bybit. ' +
    'Столбики — перевес каждой свечи, линия — сколько накопилось с начала суток. ' +
    'Табло пишет словами, что происходит, а треугольники на цене отмечают, где движение идёт без поддержки.',
  author: 'specter',
  placement: 'pane',
  orderFlow: true,
  requires: 'liquidations',
  params: [
    SOURCE,
    {
      key: 'anchor',
      label: 'Линия считает',
      hint: 'С какого момента копить перевес. Сутки — видно, кто побеждает сегодня; вся история — общий итог с начала сбора.',
      type: 'select',
      default: 'day',
      options: [
        { value: 'day', label: 'С начала суток (UTC)' },
        { value: 'week', label: 'С начала недели' },
        { value: 'all', label: 'За всю историю' },
      ],
    },
    {
      key: 'marks',
      label: 'Отметки на цене',
      hint: 'Треугольник там, где цена обновила максимум без новых покупок (или минимум без новых продаж) — движение может выдохнуться.',
      type: 'boolean',
      default: true,
    },
    TABLE_PARAM,
  ],
  outputs: [
    { key: 'delta', label: 'Перевес свечи', type: 'histogram', defaultColor: COLORS.buy, format: 'money' },
    {
      key: 'cumulative',
      label: 'Накопленный перевес',
      type: 'line',
      defaultColor: '#6E8BFF',
      lineWidth: 2,
      format: 'money',
      // Накопленное за период против перевеса одной свечи: в своей
      // панели у каждого своя шкала с цифрами.
      ownPane: true,
    },
  ],
  scale: { levels: [{ value: 0, color: '#7C8DB5', dashed: true }] },
  legend: [
    {
      sample: 'solid',
      term: 'Столбики — ',
      text: 'перевес каждой свечи. Зелёный вверх — по рынку больше покупали, красный вниз — больше продавали.',
    },
    {
      sample: 'soft',
      term: 'Линия — ',
      text: 'сколько перевеса накопилось с начала суток (период меняется в настройках). Растёт — покупатели берут верх.',
    },
    {
      sample: 'strong',
      term: 'Треугольник на цене — ',
      text: 'расхождение: цена обновила максимум, а покупок не прибавилось (или минимум — без новых продаж). Движение может выдохнуться.',
    },
  ],
  warmup: () => 0,
  calc: (candles, p, context) => {
    const explained = explainedOf(candles, p, context);
    const delta: IndicatorPoint[] = [];
    const cumulative: IndicatorPoint[] = [];
    candles.forEach((candle, i) => {
      const value = explained.delta[i] ?? null;
      delta.push(
        value === null
          ? { time: candle.time, value: null }
          : { time: candle.time, value, color: value >= 0 ? COLORS.buy : COLORS.sell },
      );
      cumulative.push({ time: candle.time, value: explained.anchored[i] ?? null });
    });
    return { delta, cumulative };
  },
  shapes: (candles, p, context) => (p.marks === false ? [] : explainedOf(candles, p, context).marks),
  readout: (candles, p, context) => explainedOf(candles, p, context).rows,
  label: () => 'Дельта по сделкам',
};
